import { JSX, useCallback, useMemo } from 'react';

/**
 * Hook to format human readable text with color coding
 * - Text between **markers** is rendered in blue (conditions)
 * - Text between ##markers## is rendered in green (actions)
 * - Plain text remains unstyled
 *
 * @param text - The text to format with **condition** and ##action## markers
 * @returns Array of JSX elements with appropriate styling
 */
export function useHumanReadableFormatter(
  text?: string
): JSX.Element[] | undefined {
  const renderColoredText = useCallback(
    (text?: string): JSX.Element[] | undefined => {
      if (!text) return undefined;
      const parts: JSX.Element[] = [];
      let currentIndex = 0;
      let key = 0;

      // Match **text** for conditions and ##text## for actions
      const regex = /(\*\*.*?\*\*|##.*?##)/g;
      let match;

      while ((match = regex.exec(text)) !== null) {
        // Add text before match
        if (match.index > currentIndex) {
          parts.push(
            <span key={key++}>{text.slice(currentIndex, match.index)}</span>
          );
        }

        // Add colored match
        const matchedText = match[0];
        if (matchedText.startsWith('**')) {
          // Condition - blue
          parts.push(
            <span
              key={key++}
              className='font-semibold text-blue-600 dark:text-blue-400'
            >
              {matchedText.slice(2, -2)}
            </span>
          );
        } else if (matchedText.startsWith('##')) {
          // Action - green
          parts.push(
            <span
              key={key++}
              className='font-semibold text-green-600 dark:text-green-400'
            >
              {matchedText.slice(2, -2)}
            </span>
          );
        }

        currentIndex = match.index + matchedText.length;
      }

      // Add remaining text
      if (currentIndex < text.length) {
        parts.push(<span key={key++}>{text.slice(currentIndex)}</span>);
      }

      return parts;
    },
    []
  );

  // Memoized colored text
  const coloredText = useMemo(
    () => renderColoredText(text),
    [text, renderColoredText]
  );

  return coloredText;
}
